import { useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import type { AnimationDirection } from 'lottie-web';
import { AnimationPlayerProps, LottieAnimationData } from "../../types";
import getAnimationData from './getAnimationData';
import './LottiePlayer.css';

interface LottiePlayerProps extends AnimationPlayerProps {
  rate: number;
  autoPlay: boolean;
  paused: boolean;
  fallbackSrc: string;
  duration: number;
  loop?: boolean;
  autoplay?: boolean;
  direction?: AnimationDirection;
  speed?: number;
  ariaLabel: string;
  ariaDescription: string;
  // width: number;
  // height: number;
  // scale: number;
  action: (jsonData: LottieAnimationData) => void;
}

export const LottiePlayer = (props: LottiePlayerProps): JSX.Element => {
  const {
    url,
    hash,
    name,
    onComplete,
    paused,
    action,
    onStart,
    direction,
    speed,
    loop,
    autoplay,
    ariaLabel,
    ariaDescription,
    // width,
    // height,
    // scale,
  } = props;

  const playerRef = useRef<any>();

  const [loaded, setLoaded] = useState<string | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const [animationData, setAnimationData] = useState<LottieAnimationData | null>(null);

  const handleOnInit = (jsonData:any) => {
    action && action(jsonData);
    console.log('LOTTIE JSON DATA', jsonData);
    setAnimationData(jsonData);
    setPlaying(hash);
  }

  const loadAnimationData = async (url:any) => {
    return await getAnimationData(url);
  };

  // initialize player on mount
  useEffect(() => {
    if (loaded !== hash) {
      setLoaded(hash);
      loadAnimationData(url).then(handleOnInit);
    }
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [hash, url, loaded]);

  // handle pausing
  useEffect(() => {
    if (!playerRef.current) return;
    if (paused && playing) {
      setPlaying(null);
    }
  }, [playing, paused]);

  // handle playing
  useEffect(() => {
    if (!playerRef.current) return;
    if (!paused && !playing) {
      setPlaying(hash);
    }
  }, [hash, paused, playing]);

  // handle direction
  useEffect(() => {
    if (!playerRef.current || !playerRef.current.animationData) return;
    if(playerRef.current.animationData.playDirection !==direction) {
      playerRef.current.setDirection(direction);
    }
  }, [hash, direction]);

  // handle speed
  useEffect(() => {
    if (!playerRef.current || !playerRef.current.animationData) return;
    if(playerRef.current.animationData.playSpeed !== speed) {
      playerRef.current.setSpeed(speed);
    }
  }, [hash, speed]);

  return (
    <div
      className="lottie-player-container"
      style={{width: "100%" ,height:"100%"}}
    >
      <Lottie
        {...{
          animationData,
          autoplay,
          name: name,
          loop,
          lottieRef: playerRef,
          onLoopComplete: () => {
            if(loop) return;
            const duration = playerRef.current.getDuration(true);
            playerRef.current.goToAndStop(duration, true);
            playerRef.current.pause();
          },
          onComplete: () => {
            onComplete(hash);
          },
          onDOMLoaded: () => {
            onStart(hash);
          },
          renderer: 'svg',
          rendererSettings: {
            preserveAspectRatio: 'xMidYMid slice',
            hideOnTransparent: false,
            progressiveLoad: true,
          },
          style:{width: "100%" ,height:"100%"},
          "aria-hidden": true   // added because some lottie animations contain purely presentation images not meant for screen readers
        }}
        {...(ariaDescription && { 'aria-describedby': 'animation-description' })}
      />
      {ariaLabel && (
        <div className="pseudo-aria-label-description" role="img" aria-label={ariaLabel}>
          {ariaLabel}
        </div>
      )}
      {ariaDescription && (
        <div id="animation-description" className="pseudo-aria-label-description">
          {ariaDescription}
        </div>
      )}
    </div>
  );
};

LottiePlayer.defaultProps = {
  loop: false,
  autoplay: true,
  direction: 1,
  speed: 1,
  onStart: () => {},
};

LottiePlayer.whyDidYouRender = true;

export default LottiePlayer;
